import {
  Box,
  Paper,
  Typography,
  CircularProgress,
  useTheme,
  Card,
  CardMedia,
  Dialog,
} from "@mui/material";
import { useEffect, useState, useRef, useCallback } from "react";
import { useSupabase } from "../auth/useSupabase";
import { motion, AnimatePresence } from "framer-motion";
import { gameService } from "../data/gameService";

// Type for attachment with URL for display
interface AttachmentWithUrl {
  id: string;
  url: string;
}

interface StandardQuestionDisplayProps {
  question: string;
  answer: string;
  questionId: string;
  showQuestion: boolean;
  showAnswer: boolean;
}

// Create motion variants for animations
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.2,
    },
  },
  exit: {
    opacity: 0,
    transition: {
      duration: 0.3,
    },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.5,
    },
  },
  exit: {
    opacity: 0,
    y: -20,
    transition: {
      duration: 0.3,
    },
  },
};

const questionVariants = {
  hidden: { opacity: 0, scale: 0.95 },
  visible: {
    opacity: 1,
    scale: 1,
    transition: {
      duration: 0.4,
      ease: "easeOut",
    },
  },
  exit: {
    opacity: 0,
    scale: 0.95,
    transition: {
      duration: 0.3,
    },
  },
};

const answerVariants = {
  hidden: { opacity: 0, y: 30 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      type: "spring",
      stiffness: 300,
      damping: 25,
    },
  },
  exit: {
    opacity: 0,
    y: 30,
    transition: {
      duration: 0.3,
    },
  },
};

// Map Material components to motion components
const MotionBox = motion(Box);
const MotionPaper = motion(Paper);
const MotionCard = motion(Card);

const StandardQuestionDisplay = (props: StandardQuestionDisplayProps) => {
  const { question, showAnswer, showQuestion, answer, questionId } = props;
  const [attachments, setAttachments] = useState<AttachmentWithUrl[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const [isExpandImages, setIsExpandImages] = useState<boolean>(false);

  const { supabase } = useSupabase();
  const theme = useTheme();

  const prevQuestionIdRef = useRef<string>("");

  const loadAttachments = useCallback(async () => {
    if (!questionId) return;
    setIsLoading(true);
    try {
      const attachmentRows = await gameService.getQuestionAttachments(supabase, questionId);
      if (!attachmentRows || attachmentRows.length === 0) {
        setAttachments([]);
        return;
      }

      const fetchedAttachments = await Promise.all(
        attachmentRows.map(async (att) => {
          const { data, error } = await supabase.storage
            .from("dozen-attachments")
            .createSignedUrl(att.file_path, 3600);
          if (error) {
            console.error("Error getting signed URL:", error);
            return { id: att.id, url: "" };
          }
          return { id: att.id, url: data?.signedUrl || "" };
        }),
      );

      setAttachments(fetchedAttachments.filter((a) => a.url));
    } catch (error) {
      console.error("Error loading attachments:", error);
    } finally {
      setIsLoading(false);
    }
  }, [questionId, supabase]);

  useEffect(() => {
    if (showQuestion && questionId && questionId !== prevQuestionIdRef.current) {
      loadAttachments();
      prevQuestionIdRef.current = questionId;
    }
    return () => {
      if (!showQuestion) {
        prevQuestionIdRef.current = "";
      }
    };
  }, [questionId, showQuestion, loadAttachments]);

  // Get letter label for attachment (a, b, c, etc.)
  const getLetterLabel = (index: number): string => {
    return String.fromCharCode(97 + index); // 97 is the ASCII code for 'a'
  };

  const renderImages = (isModal: boolean) => (
    <AnimatePresence>
      {attachments.map((attachment, index) => (
        <MotionCard
          key={attachment.id}
          onClick={() => {
            if (!isModal) {
              setIsExpandImages(true);
            }
          }}
          variants={itemVariants}
          custom={index}
          whileHover={{
            scale: 1.02,
            boxShadow: theme.shadows[6],
            y: -4,
          }}
          elevation={3}
          sx={{
            borderRadius: 2,
            overflow: "hidden",
            border: `1px solid ${theme.palette.primary.light}`,
            width: {
              xs: "100%",
              sm: attachments.length === 1 ? "100%" : "45%",
              md: attachments.length === 1 ? "100%" : "30%",
            },
            maxWidth: isModal ? "90vw" : "510px",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <CardMedia
            component="img"
            image={attachment.url}
            alt={`Attachment ${getLetterLabel(index)}`}
            sx={{
              width: "100%",
              objectFit: "contain",
              height: isModal ? "90vh" : "400px",
              maxHeight: isModal ? "90vh" : "400px",
            }}
          />
          <Box
            sx={{
              backgroundColor: theme.palette.primary.main,
              color: "#fff",
              fontWeight: "bold",
              padding: "4px 8px",
              textAlign: "center",
              textTransform: "uppercase",
            }}
          >
            <Typography variant="body2" fontWeight="bold">
              {getLetterLabel(index)}
            </Typography>
          </Box>
        </MotionCard>
      ))}
    </AnimatePresence>
  );

  return (
    <MotionBox
      sx={{
        width: "100%",
        display: "flex",
        flex: 1,
        height: "100%",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        px: 1,
        gap: "8px",
      }}
      initial="hidden"
      animate="visible"
      exit="exit"
      variants={containerVariants}
    >
      {/* Attachments Section */}
      <AnimatePresence>
        {showQuestion && attachments.length > 0 && (
          <MotionBox
            key="attachments-container"
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
            sx={{
              width: "100%",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
            }}
          >
            {isLoading ? (
              <MotionBox
                variants={itemVariants}
                sx={{ display: "flex", justifyContent: "center", my: 4 }}
              >
                <CircularProgress color="primary" />
              </MotionBox>
            ) : (
              <MotionBox
                variants={containerVariants}
                sx={{
                  display: "flex",
                  flexWrap: "wrap",
                  justifyContent: "center",
                  gap: 2,
                  width: "100%",
                }}
              >
                {renderImages(false)}
              </MotionBox>
            )}
          </MotionBox>
        )}
      </AnimatePresence>

      {/* Question Display */}
      <AnimatePresence>
        {showQuestion && (
          <MotionPaper
            key="question-paper"
            variants={questionVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
            elevation={3}
            sx={{
              width: "fit-content",
              backgroundColor: theme.palette.background.paper,
              borderRadius: 2,
              borderLeft: `4px solid ${theme.palette.primary.main}`,
              px: 4,
              py: 1,
              position: "relative",
            }}
          >
            <Typography
              variant="h5"
              sx={{
                fontWeight: "500",
                lineHeight: 1.6,
                p: 1,
                borderRadius: 1,
                color: theme.palette.text.primary,
                position: "relative",
                textAlign: "center",
              }}
            >
              {question}
            </Typography>
          </MotionPaper>
        )}
      </AnimatePresence>

      {/* Answer Display */}
      <AnimatePresence>
        {showAnswer && (
          <MotionPaper
            key="answer-paper"
            variants={answerVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
            elevation={3}
            sx={{
              width: "fit-content",
              backgroundColor: theme.palette.success.light,
              borderRadius: 2,
              borderLeft: `4px solid ${theme.palette.success.main}`,
              px: 4,
              py: 1,
              position: "relative",
            }}
          >
            <Typography
              variant="h5"
              sx={{
                fontWeight: "500",
                lineHeight: 1.6,
                p: { xs: 1.5, sm: 2 },
                borderRadius: 1,
                color: theme.palette.text.primary,
                textAlign: "center",
              }}
            >
              {answer}
            </Typography>
          </MotionPaper>
        )}
      </AnimatePresence>

      {/* Image Viewer */}
      <Dialog
        open={isExpandImages}
        fullScreen
        onClose={() => setIsExpandImages(false)}
      >
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            justifyContent: "center",
            gap: 2,
            width: "100%",
          }}
        >
          {renderImages(true)}
        </Box>
      </Dialog>
    </MotionBox>
  );
};

export default StandardQuestionDisplay;
